import React, { useState } from "react";
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    Typography, TextField, FormControl, FormControlLabel,
    RadioGroup, Radio, FormGroup, Checkbox,
} from '@mui/material';
import { getAllUnansweredMdlSeqs, NUMBER_ONLY_SEQS } from './SurveyRoles';

const SECTION_MDL_TITLES = {
    28: '모델 기본정보',
    34: '설비 수집 데이터',
    38: 'RTU 운영 로그',
    40: 'RTU <-> 공단REMS',
    42: 'RTU <-> 설비',
    45: '운영 상태'
};

function SurveyMdlModal({ open, mdlQuestions, answerMap, onClose, onSave }) {

    const [mdlNm, setMdlNm] = useState('');
    const [mdlNmError, setMdlNmError] = useState(false);
    const [answers, setAnswers] = useState({});
    const [extraTexts, setExtraTexts] = useState({});
    const [unansweredSet, setUnansweredSet] = useState(new Set());

    const handleCheckboxChange = (surveySeq, answerNo) => {
        setAnswers(prev => {
            const current = prev[surveySeq] || [];
            const updated = current.includes(answerNo)
                ? current.filter(v => v !== answerNo)
                : [...current, answerNo];
            return { ...prev, [surveySeq]: updated };
        });
    };

    const handleSave = () => {
        if (!mdlNm.trim()) {
            setMdlNmError(true);
            const el = document.getElementById('mdl-dialog-content');
            if (el) el.scrollTop = 0;
            return;
        }
        const unansweredSeqs = getAllUnansweredMdlSeqs(mdlQuestions, answers);
        if (unansweredSeqs.length > 0) {
            setUnansweredSet(new Set(unansweredSeqs));
            const el = document.getElementById(`mdl-q-${unansweredSeqs[0]}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        setUnansweredSet(new Set());
        onSave({ mdlNm: mdlNm.trim(), answers, extraTexts });
        handleReset();
    };

    const handleReset = () => {
        setMdlNm('');
        setMdlNmError(false);
        setAnswers({});
        setExtraTexts({});
        setUnansweredSet(new Set());
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const renderAnswer = (q) => {
        const choices = answerMap[q.surveySeq] || [];
        const isNumberOnly = NUMBER_ONLY_SEQS.includes(String(q.surveySeq));

        if (q.answerType === 'TEXT') {
            return (
                <TextField
                    multiline={!isNumberOnly}
                    rows={isNumberOnly ? 1 : 3}
                    fullWidth
                    placeholder={isNumberOnly ? '숫자만 입력해 주세요.' : '답변을 입력해 주세요.'}
                    inputProps={isNumberOnly ? { inputMode: 'numeric' } : {}}
                    value={answers[q.surveySeq] || ''}
                    onChange={(e) => {
                        const val = isNumberOnly ? e.target.value.replace(/[^0-9]/g, '') : e.target.value;
                        setAnswers(prev => ({ ...prev, [q.surveySeq]: val }));
                    }}
                />
            );
        }

        if (q.answerType === 'RADIO') {
            return (
                <FormControl component="fieldset">
                    <RadioGroup
                        value={answers[q.surveySeq] || ''}
                        onChange={(e) => setAnswers(prev => ({ ...prev, [q.surveySeq]: e.target.value }))}
                    >
                        {choices.map(ans => (
                            <Box key={ans.answerNo}>
                                <FormControlLabel
                                    value={ans.answerNo}
                                    control={<Radio />}
                                    label={ans.answerContent}
                                />
                                {ans.hasText === 'Y' && answers[q.surveySeq] === ans.answerNo && (
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder={isNumberOnly ? '숫자만 입력해 주세요.' : '내용을 입력해 주세요.'}
                                        inputProps={isNumberOnly ? { inputMode: 'numeric' } : {}}
                                        value={extraTexts[`${q.surveySeq}_${ans.answerNo}`] || ''}
                                        onChange={(e) => {
                                            const val = isNumberOnly ? e.target.value.replace(/[^0-9]/g, '') : e.target.value;
                                            setExtraTexts(prev => ({ ...prev, [`${q.surveySeq}_${ans.answerNo}`]: val }));
                                        }}
                                        sx={{ mb: 1 }}
                                    />
                                )}
                            </Box>
                        ))}
                    </RadioGroup>
                </FormControl>
            );
        }

        if (q.answerType === 'CHECKBOX') {
            return (
                <FormGroup>
                    {choices.map(ans => (
                        <Box key={ans.answerNo}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={(answers[q.surveySeq] || []).includes(ans.answerNo)}
                                        onChange={() => handleCheckboxChange(q.surveySeq, ans.answerNo)}
                                    />
                                }
                                label={ans.answerContent}
                            />
                            {ans.hasText === 'Y' && (answers[q.surveySeq] || []).includes(ans.answerNo) && (
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder={isNumberOnly ? '숫자만 입력해 주세요.' : '내용을 입력해 주세요.'}
                                    inputProps={isNumberOnly ? { inputMode: 'numeric' } : {}}
                                    value={extraTexts[`${q.surveySeq}_${ans.answerNo}`] || ''}
                                    onChange={(e) => {
                                        const val = isNumberOnly ? e.target.value.replace(/[^0-9]/g, '') : e.target.value;
                                        setExtraTexts(prev => ({ ...prev, [`${q.surveySeq}_${ans.answerNo}`]: val }));
                                    }}
                                    sx={{ mb: 1 }}
                                />
                            )}
                        </Box>
                    ))}
                </FormGroup>
            );
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullScreen>
            <DialogTitle sx={{ bgcolor: '#334475', color: '#fff', py: 1.5 }}>
                설비 설문 등록
            </DialogTitle>
            <DialogContent id="mdl-dialog-content" dividers sx={{ px: 3, pt: 3 }}>

                {/* 설비명 입력 */}
                <Box sx={{ mb: 4 }}>
                    <TextField
                        label="설비명 *"
                        size="small"
                        fullWidth
                        value={mdlNm}
                        onChange={(e) => { setMdlNm(e.target.value); setMdlNmError(false); }}
                        error={mdlNmError}
                        helperText={mdlNmError ? '설비명은 필수입니다.' : ''}
                    />
                </Box>

                {/* MDL 질문 목록 */}
                {mdlQuestions.map((q, idx) => (
                    <React.Fragment key={q.surveySeq}>
                        {SECTION_MDL_TITLES[q.ord] && (
                            <Box sx={{ backgroundColor: '#33754a',  px: 2, py: 1.5, borderRadius: 1, mb: 2 }}>
                                <Typography fontWeight="bold" color="#fff" sx={{ fontSize: 15 }}>{SECTION_MDL_TITLES[q.ord]}</Typography>
                            </Box>
                        )}
                    <Box
                        id={`mdl-q-${q.surveySeq}`}
                        sx={{
                            mb: 3, p: 1.5, borderRadius: 2,
                            border: unansweredSet.has(q.surveySeq) ? '1.5px solid #d32f2f' : '1px solid #e0e0e0',
                            backgroundColor: unansweredSet.has(q.surveySeq) ? '#fff5f5' : 'background.paper',
                        }}
                    >
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {idx + 1} / {mdlQuestions.length}
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 0.5, mb: 2 }}>
                            <span style={{ color: '#33754a', fontWeight: 700 }}>Q{q.ord}.</span> {q.question}
                        </Typography>
                        {unansweredSet.has(q.surveySeq) && (
                            <Typography sx={{ fontSize: 12, color: '#d32f2f', mb: 1 }}>
                                ※ 필수 응답 항목입니다.
                            </Typography>
                        )}
                        {renderAnswer(q)}
                    </Box>
                    </React.Fragment>
                ))}

            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button variant="outlined" onClick={handleClose}>취소</Button>
                <Button variant="contained" onClick={handleSave}>저장</Button>
            </DialogActions>
        </Dialog>
    );
}

export default SurveyMdlModal;
